import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { WS_BASE_URL, USE_MOCK } from '../config/api';
import { DriverCheckin } from '../types';
import { encrypt } from '../utils/crypto'; // 👈 Importando a função de criptografia
import { generateMockQueue, generateMockHistory, mockDelay } from '../utils/mockData';



export const useBillingPanel = (base?: string) => {
  const [queue, setQueue] = useState<DriverCheckin[]>([]);
  const [history, setHistory] = useState<DriverCheckin[]>([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());


  const fetchDriverStatus = async (codTransp: string, cpf?: string) => {
    if (!codTransp && !cpf) return { status: 'Transportadora inválida' };

    const storedDeposito = localStorage.getItem('selectedDeposito');
    if (!storedDeposito) return { status: 'Depósito não selecionado' };

    const deposito = JSON.parse(storedDeposito);
    try {

      const { data } = await api.get(`/order-status/${encodeURIComponent(codTransp ?? '-')}`, {
        params: {
          deposito_id: deposito.cod_dep,
          cpf: encrypt(cpf ?? ''), // 👈 Enviando o CPF criptografado
        },
      });

      return {
        status: data.order_status ?? data.status ?? data.StatusGeral ?? 'Não há pedido emitido',
        cliente_nome: data.cliente_nome ?? '',
        numeroPedido: data.pedido_numero ?? null,
      };
    } catch {
      return { status: 'Erro ao obter status' };

    }
  };


  const enrichCheckinsWithOrderStatus = async (checkins: DriverCheckin[]) => {
    if (!checkins?.length) return checkins;


    const uniqueTransps = Array.from(new Set(checkins.map(c => c.cod_transp).filter(Boolean)));

    const statusCache = new Map<

      string,
      { status: string; cliente_nome?: string; numeroPedido?: number | null }
    >();

    await Promise.all(
      uniqueTransps.map(async codTransp => {

        // 🔹 Pega o primeiro check-in com esse codTransp (para ter acesso ao CPF)
        const checkin = checkins.find(c => c.cod_transp === codTransp);
        const statusInfo = await fetchDriverStatus(codTransp, checkin?.cpf);
        statusCache.set(codTransp, statusInfo);

      })
    );

    return checkins.map(ci => ({
      ...ci,

      order_status: statusCache.get(ci.cod_transp)?.status ?? 'Não há pedido emitido',
      cliente_nome: statusCache.get(ci.cod_transp)?.cliente_nome ?? ci.cliente_nome ?? '',
      numeroPedido: statusCache.get(ci.cod_transp)?.numeroPedido ?? undefined,

    }));

  };

  // ------------------- FETCH QUEUE -------------------
  const fetchQueue = async () => {
    try {
      if (USE_MOCK) {
        await mockDelay(500);

        setQueue(generateMockQueue());

        setLoading(false);

        return;
      }


      // Sempre pegar apenas o dia de hoje do backend
      const dateToday = new Date().toISOString().split('T')[0];
      const { data } = await api.get<DriverCheckin[]>('/checkins', {
        params: { date: dateToday },
      });

      const filteredQueue = data.filter(d => d.status === 'Em Fila');

      const enrichedQueue = await enrichCheckinsWithOrderStatus(filteredQueue);

      setQueue(enrichedQueue);
      if (filteredQueue.length === 0) setError('Nenhum motorista na fila');
    } catch (err) {
      console.error(err);
      setError('Erro ao buscar fila');
    } finally {

      setLoading(false);
    }
  };

  // ------------------- FETCH HISTORY -------------------
  const fetchHistory = async (date?: Date | null) => {
    try {
      if (USE_MOCK) {
        await mockDelay(500);
        setHistory(generateMockHistory());
        return;

      }



      const dateString = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

      const { data } = await api.get<DriverCheckin[]>('/checkins', {
        params: { date: dateString },
      });

      // Apenas status Cancelado ou Finalizado

      const filteredHistory = data.filter(d => ['Cancelado', 'Finalizado'].includes(d.status));
      const enrichedHistory = await enrichCheckinsWithOrderStatus(filteredHistory);

      setHistory(enrichedHistory);
    } catch (err) {
      console.error(err);
    }
  };


  // ------------------- HANDLERS -------------------
  const handleFinishDriver = async (id: number, observation?: string) => {
    setActionLoading(id);

    try {
      if (USE_MOCK) {

        const driver = queue.find(d => d.id === id);
        if (driver) {
          setQueue(prev => prev.filter(d => d.id !== id));
          setHistory(prev => [{ ...driver, status: 'Finalizado' }, ...prev]);
        }
        return;
      }
      const storedDeposito = localStorage.getItem('selectedDeposito');
      if (!storedDeposito) throw new Error('Nenhum depósito selecionado');
      const deposito = JSON.parse(storedDeposito);

      await api.put(`/checkin/${id}/status/${deposito.cod_dep}`, { status: 'Finalizado', observation: observation });

      await fetchQueue();
      await fetchHistory();
    } catch (err) {

      console.error(err);
      alert('Erro ao finalizar atendimento');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelDriver = async (id: number, reason: string) => {
    setActionLoading(id);
    try {
      if (USE_MOCK) {
        const driver = queue.find(d => d.id === id);
        if (driver) {
          setQueue(prev => prev.filter(d => d.id !== id));
          setHistory(prev => [{ ...driver, status: 'Cancelado', cancel_reason: reason }, ...prev]);
        }
        return;
      }

      const storedDeposito = localStorage.getItem('selectedDeposito');
      if (!storedDeposito) throw new Error('Nenhum depósito selecionado');
      const deposito = JSON.parse(storedDeposito);


      await api.put(`/checkin/${id}/status/${deposito.cod_dep}`, { status: 'Cancelado', cancel_reason: reason });

      await fetchQueue();
      await fetchHistory();
    } catch (err) {
      console.error(err);
      alert('Erro ao cancelar atendimento');
    } finally {
      setActionLoading(null);
    }
  };

  // ------------------- WEBSOCKET & TIMERS -------------------
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const initializePanel = async () => {
      await fetchQueue();
      await fetchHistory(selectedDate);

      if (!USE_MOCK) {
        const ws = new WebSocket(WS_BASE_URL);
        wsRef.current = ws;

        ws.onopen = () => setConnected(true);
        ws.onmessage = () => { fetchQueue(); fetchHistory(selectedDate); };

        ws.onerror = () => setConnected(false);

        ws.onclose = () => setConnected(false);

      } else {
        setConnected(true);
      }
    };
    initializePanel();


    const interval = setInterval(() => {
      fetchQueue();
      fetchHistory(selectedDate);
    }, 60000);


    return () => clearInterval(interval);
  }, [base, selectedDate]);

  const calculateWaitTime = (checkinTime: string): string => {

    const now = new Date();
    const checkin = new Date(checkinTime);
    const diffMs = now.getTime() - checkin.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    queue,
    history,
    loading,
    connected,
    actionLoading,
    error,
    setActionLoading,

    setQueue,
    setHistory,
    fetchQueue,
    fetchHistory,
    calculateWaitTime,
    handleFinishDriver,
    handleCancelDriver,
    USE_MOCK,
    selectedDate,
    setSelectedDate,
  };
};
